/* @img-migrated */
import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { User, ArrowRight, Crown, Award, Users, Briefcase, Quote } from 'lucide-react';

import Img from '../components/Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ═══════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════ */
const NAVY      = '#002249';
const NAVY_DEEP = '#0A1628';
const RED       = '#CD1125';
const GOLD      = '#C5A059';
const SLATE     = '#1D3557';

const HERO_TEXTURE = 'https://static.prod-images.emergentagent.com/jobs/b95727ce-907c-4bae-a008-7be042e57719/images/bccb6f276bb5c8c46f206ee9d1ebcc2a2252855fb7572ce8dfd8829573f11dfe.png';
const MARBLE_TEX   = 'https://static.prod-images.emergentagent.com/jobs/b95727ce-907c-4bae-a008-7be042e57719/images/afc64bb0dff41b2b825a1f8542a8ad39fcf8ea2a65fb518a953fd662f281093d.png';

const CATEGORY_META = {
  founder:   { label: 'Our Founder',          subtitle: 'The visionary behind our institution',                    icon: Crown,     accent: RED },
  executive: { label: 'Executive Board',       subtitle: 'Strategic leadership driving our mission forward',        icon: Award,     accent: NAVY },
  director:  { label: 'Board of Directors',    subtitle: 'Guiding the Chamber with expertise and dedication',       icon: Users,     accent: '#457B9D' },
  committee: { label: 'Committee Directors',   subtitle: 'Specialized leaders shaping our programs and initiatives', icon: Briefcase, accent: SLATE },
};

/* ═══════════════════════════════════════════
   GLOBAL KEYFRAMES (injected once)
   ═══════════════════════════════════════════ */
const GLOBAL_STYLES_ID = 'leadership-ribbon-styles';
const injectGlobalStyles = () => {
  if (document.getElementById(GLOBAL_STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = GLOBAL_STYLES_ID;
  style.textContent = `
    @keyframes ribbonDrift1 {
      0%   { transform: translateX(0px) translateY(0px); }
      33%  { transform: translateX(50px) translateY(-18px); }
      66%  { transform: translateX(-15px) translateY(10px); }
      100% { transform: translateX(0px) translateY(0px); }
    }
    @keyframes ribbonDrift2 {
      0%   { transform: translateX(0px) translateY(0px); }
      33%  { transform: translateX(-40px) translateY(15px); }
      66%  { transform: translateX(20px) translateY(-8px); }
      100% { transform: translateX(0px) translateY(0px); }
    }
    @keyframes ribbonDrift3 {
      0%   { transform: translateX(0px) translateY(0px); }
      33%  { transform: translateX(35px) translateY(14px); }
      66%  { transform: translateX(-25px) translateY(-6px); }
      100% { transform: translateX(0px) translateY(0px); }
    }
    @keyframes ribbonDrift4 {
      0%   { transform: translateX(0px) translateY(0px) rotate(0deg); }
      50%  { transform: translateX(-30px) translateY(-20px) rotate(0.5deg); }
      100% { transform: translateX(0px) translateY(0px) rotate(0deg); }
    }
    @keyframes subtleGlow {
      0%   { opacity: 0.6; }
      50%  { opacity: 1; }
      100% { opacity: 0.6; }
    }
  `;
  document.head.appendChild(style);
};

/* ═══════════════════════════════════════════
   ANIMATION HELPERS
   ═══════════════════════════════════════════ */
const Reveal = ({ children, className = '', delay = 0, y = 32 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
};

const RevealLine = ({ delay = 0, color = GOLD, width = '80px' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-20px' });
  return (
    <motion.div ref={ref} initial={{ scaleX: 0 }} animate={inView ? { scaleX: 1 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      style={{ width, height: '2px', backgroundColor: color, transformOrigin: 'left' }} />
  );
};

/* ═══════════════════════════════════════════
   FLOWING RIBBON BACKGROUND
   Continuous red/white/blue ribbon layer
   that spans the entire content area
   ═══════════════════════════════════════════ */
const FlowingRibbons = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">

    {/* ── RED RIBBONS — upper zone, simple elegant curves ── */}
    <svg className="absolute w-[200%] h-full top-0 left-[-10%]" viewBox="0 0 2800 1200" fill="none" preserveAspectRatio="none"
      style={{ animation: 'ribbonDrift1 22s ease-in-out infinite', opacity: 0.12 }}>
      <path d="M-100 80C200 30 450 180 750 130S1100 20 1400 100S1800 210 2100 150S2500 70 2900 120" stroke={RED} strokeWidth="2.5" strokeLinecap="round" />
      <path className="hidden sm:block" d="M-100 130C250 80 500 220 800 170S1150 60 1450 140S1850 250 2150 190S2550 110 2900 160" stroke={RED} strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
      <path className="hidden sm:block" d="M-100 180C300 120 550 260 850 210S1200 100 1500 180S1900 290 2200 230S2600 150 2900 200" stroke={RED} strokeWidth="1" strokeLinecap="round" opacity="0.4" />
    </svg>

    {/* ── BLUE/NAVY RIBBON — single elegant lower curve ── */}
    <svg className="absolute w-[200%] h-full top-0 left-[-8%]" viewBox="0 0 2800 1200" fill="none" preserveAspectRatio="none"
      style={{ animation: 'ribbonDrift3 25s ease-in-out infinite', opacity: 0.10 }}>
      <path d="M-100 650C250 590 550 720 850 670S1200 560 1500 640S1850 760 2150 700S2550 620 2900 680" stroke={NAVY} strokeWidth="2.5" strokeLinecap="round" />
      <path className="hidden sm:block" d="M-100 700C300 640 600 770 900 720S1250 610 1550 690S1900 810 2200 750S2600 670 2900 730" stroke={SLATE} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
    </svg>

    {/* ── CORNER ACCENTS — desktop only ── */}
    <svg className="hidden sm:block absolute top-0 right-0 w-[800px] h-[600px]" viewBox="0 0 800 600" fill="none"
      style={{ animation: 'ribbonDrift4 32s ease-in-out infinite', opacity: 0.09 }}>
      <path d="M800 0C700 80 550 160 400 200S200 280 100 350S30 450 0 550" stroke={RED} strokeWidth="2" strokeLinecap="round" />
      <path d="M800 40C680 120 520 190 380 240S180 320 80 390S10 490 0 580" stroke={NAVY} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
    </svg>
    <svg className="hidden sm:block absolute bottom-0 left-0 w-[700px] h-[500px]" viewBox="0 0 700 500" fill="none"
      style={{ animation: 'ribbonDrift2 30s ease-in-out infinite', opacity: 0.08 }}>
      <path d="M0 500C80 420 180 340 300 280S460 200 570 140S650 60 700 0" stroke={RED} strokeWidth="2" strokeLinecap="round" />
      <path d="M0 460C100 380 210 300 330 240S490 160 600 100S680 30 700 0" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
    </svg>

    {/* ── CROSSOVER — desktop only ── */}
    <svg className="hidden sm:block absolute w-[200%] h-full top-0 left-[-6%]" viewBox="0 0 2800 1200" fill="none" preserveAspectRatio="none"
      style={{ animation: 'ribbonDrift1 33s ease-in-out infinite', opacity: 0.08 }}>
      <path d="M-100 920C400 850 700 990 1000 930S1400 830 1700 910S2100 1030 2400 970S2700 890 2900 940" stroke={RED} strokeWidth="2" strokeLinecap="round" />
      <path d="M-100 970C350 900 700 1040 1050 980S1450 880 1750 960S2100 1080 2400 1020S2700 940 2900 990" stroke={NAVY} strokeWidth="2" strokeLinecap="round" />
    </svg>
  </div>
);

/* ═══════════════════════════════════════════
   SECTION HEADER (inline, no separation)
   ═══════════════════════════════════════════ */
const SectionHeader = ({ category }) => {
  const meta = CATEGORY_META[category];
  const Icon = meta.icon;
  return (
    <Reveal>
      <div className="flex flex-col items-center text-center mb-10 sm:mb-12">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-8 sm:w-12" style={{ backgroundColor: `${meta.accent}30` }} />
          <div className="w-9 h-9 rounded-full flex items-center justify-center border" style={{ borderColor: `${meta.accent}25` }}>
            <Icon className="w-4 h-4" style={{ color: meta.accent }} />
          </div>
          <div className="h-px w-8 sm:w-12" style={{ backgroundColor: `${meta.accent}30` }} />
        </div>
        <p className="text-[10px] sm:text-[11px] font-bold tracking-[0.25em] uppercase mb-2.5" style={{ color: meta.accent }}>
          {meta.label}
        </p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ color: NAVY }}>
          {meta.subtitle}
        </h2>
      </div>
    </Reveal>
  );
};

/* ═══════════════════════════════════════════
   THIN ELEGANT SEPARATOR (inline between sections)
   ═══════════════════════════════════════════ */
const ThinSeparator = ({ bg = 'transparent' }) => (
  <div className="relative py-2" style={{ backgroundColor: bg }}>
    <div className="flex items-center justify-center gap-2">
      <div className="h-px w-16 sm:w-24" style={{ background: `linear-gradient(to right, transparent, ${GOLD}30)` }} />
      <div className="w-1 h-1 rotate-45" style={{ backgroundColor: GOLD, opacity: 0.35 }} />
      <div className="h-px w-16 sm:w-24" style={{ background: `linear-gradient(to left, transparent, ${GOLD}30)` }} />
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
const LeadershipPage = () => {
  const [leadership, setLeadership] = useState([]);
  const [loading, setLoading] = useState(true);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    injectGlobalStyles();
    fetchLeadership();
  }, []);

  const fetchLeadership = async () => {
    try {
      const response = await axios.get(`${API}/leadership`);
      setLeadership(response.data);
    } catch (error) { /* silently handled */ }
    finally { setLoading(false); }
  };

  const getImgSrc = (url) => {
    if (!url) return null;
    return resolveImg(url);
  };

  const founder   = leadership.filter(l => l.category === 'founder');
  const executive = leadership.filter(l => l.category === 'executive');
  const directors = leadership.filter(l => l.category === 'director');
  const committee = leadership.filter(l => l.category === 'committee');

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ backgroundColor: NAVY_DEEP }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          <p className="text-white/40 text-xs tracking-[0.2em] uppercase">Loading Leadership</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="leadership-page">

      {/* ╔══════════════════════════════════════════════╗
          ║            HERO SECTION                      ║
          ╚══════════════════════════════════════════════╝ */}
      <section ref={heroRef} className="relative min-h-[520px] sm:min-h-[560px] flex items-center overflow-hidden" style={{ backgroundColor: NAVY_DEEP }} data-testid="leadership-hero">
        <div className="absolute inset-0">
          <Img variant="generic" src={HERO_TEXTURE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-overlay" loading="eager" />
          <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 80% 60% at 70% 40%, ${NAVY}90, ${NAVY_DEEP}ff)` }} />
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
        </div>
        {/* Hero ribbon accents */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] opacity-[0.06] pointer-events-none">
          <svg viewBox="0 0 600 600" fill="none" className="w-full h-full">
            <path d="M100 0C200 140 340 260 500 320S580 380 600 440" stroke={RED} strokeWidth="2.5" />
            <path d="M160 0C260 120 380 240 520 310S590 360 600 400" stroke={GOLD} strokeWidth="1.5" />
            <path d="M50 100C150 200 300 300 460 350S570 400 600 500" stroke="white" strokeWidth="1.5" />
          </svg>
        </div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] opacity-[0.04] pointer-events-none">
          <svg viewBox="0 0 400 400" fill="none" className="w-full h-full">
            <path d="M0 350C100 280 200 220 300 200S380 180 400 130" stroke={RED} strokeWidth="2" />
            <path d="M0 280C80 220 190 170 280 160S360 140 400 90" stroke="white" strokeWidth="1.5" />
          </svg>
        </div>

        <div className="container-custom relative z-10 pt-28 sm:pt-32 pb-16 sm:pb-20">
          <motion.div style={{ y: heroTextY, opacity: heroOpacity }} className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-10" style={{ backgroundColor: `${GOLD}60` }} />
                <span className="text-[10px] sm:text-[11px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>Leadership</span>
              </div>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.06] tracking-tight mb-6">
              Meet Our<br />
              <span className="relative inline-block">
                Leaders
                <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute -bottom-2 left-0 h-[3px] w-full origin-left" style={{ background: `linear-gradient(to right, ${RED}, ${RED}00)` }} />
              </span>
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
              className="text-white/50 text-sm sm:text-base lg:text-lg leading-relaxed max-w-xl">
              The visionaries, strategists, and champions behind the Dominican Chamber of Commerce of Florida — 
              united by purpose, driven by excellence, committed to building a lasting legacy.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
              className="flex items-center gap-8 mt-10">
              {[
                { n: leadership.length, l: 'Leaders' },
                { n: Object.keys(CATEGORY_META).filter(k => leadership.some(m => m.category === k)).length, l: 'Divisions' },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-2xl sm:text-3xl font-bold text-white">{s.n}</span>
                  <span className="text-[10px] tracking-[0.15em] uppercase text-white/35 font-medium">{s.l}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <div className="h-[2px]" style={{ background: `linear-gradient(to right, ${RED}00, ${RED}50, ${GOLD}40, ${RED}00)` }} />
        </div>
      </section>

      {/* ╔══════════════════════════════════════════════╗
          ║   CONTINUOUS LEADERSHIP CONTENT AREA          ║
          ║   Flowing ribbons behind all sections         ║
          ╚══════════════════════════════════════════════╝ */}
      <div className="relative" data-testid="leadership-content-area">
        {/* Flowing ribbon background — spans ALL content sections */}
        <FlowingRibbons />

        {/* Animated gradient glows — ambient color presence (desktop only) */}
        <div className="absolute inset-0 pointer-events-none hidden sm:block" style={{ animation: 'subtleGlow 12s ease-in-out infinite' }}>
          <div className="absolute top-[10%] right-[5%] w-[600px] h-[600px] rounded-full" style={{ background: `radial-gradient(circle, ${RED}0d, transparent 65%)` }} />
          <div className="absolute top-[40%] left-[0%] w-[500px] h-[500px] rounded-full" style={{ background: `radial-gradient(circle, ${NAVY}12, transparent 65%)` }} />
          <div className="absolute top-[70%] right-[15%] w-[450px] h-[450px] rounded-full" style={{ background: `radial-gradient(circle, ${RED}0a, transparent 65%)` }} />
        </div>

        {/* ── FOUNDER ── */}
        {founder.length > 0 && founder.map(leader => (
          <section key={leader.id} className="relative pt-14 sm:pt-20 pb-8 sm:pb-12 bg-transparent overflow-hidden" data-testid="founder-section">
            <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: `repeating-linear-gradient(135deg, ${NAVY} 0px, ${NAVY} 1px, transparent 1px, transparent 40px)` }} />

            <div className="container-custom relative z-10">
              <SectionHeader category="founder" />

              <Reveal>
                <Link to={`/leadership/${leader.id}`} className="group block" data-testid={`leader-${leader.id}`}>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center max-w-5xl mx-auto">
                    {/* Left — Photo */}
                    <div className="lg:col-span-5 flex justify-center lg:justify-end">
                      <div className="relative">
                        <div className="absolute -inset-3 sm:-inset-4 border border-dashed rounded-sm pointer-events-none" style={{ borderColor: `${GOLD}25` }} />
                        <div className="absolute -top-1.5 -left-1.5 w-4 h-4 border-t-2 border-l-2 rounded-tl-sm pointer-events-none" style={{ borderColor: `${RED}40` }} />
                        <div className="absolute -bottom-1.5 -right-1.5 w-4 h-4 border-b-2 border-r-2 rounded-br-sm pointer-events-none" style={{ borderColor: `${RED}40` }} />

                        <div className="relative w-[200px] sm:w-[260px] lg:w-[300px] aspect-[3/4] overflow-hidden rounded-sm shadow-xl group-hover:shadow-2xl transition-shadow duration-700">
                          {getImgSrc(leader.image_url) ? (
                            <Img variant="leader" src={getImgSrc(leader.image_url)} alt={leader.name}
                              className="w-full h-full object-cover object-[center_20%] group-hover:scale-[1.03] transition-transform duration-700" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${NAVY}, ${SLATE})` }}>
                              <User className="w-24 h-24 text-white/15" />
                            </div>
                          )}
                          <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/30 to-transparent" />
                        </div>
                      </div>
                    </div>

                    {/* Right — Info & Bio */}
                    <div className="lg:col-span-7 text-center lg:text-left">
                      <p className="text-[10px] sm:text-[11px] font-bold tracking-[0.25em] uppercase mb-3" style={{ color: RED }}>
                        {leader.title}
                      </p>
                      <h3 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" style={{ color: NAVY }}>
                        {leader.name}
                      </h3>
                      <p className="text-sm sm:text-base font-medium mb-5" style={{ color: `${NAVY}80` }}>
                        {leader.title}
                      </p>
                      <RevealLine delay={0.3} color={GOLD} width="60px" />

                      {leader.bio && (
                        <div className="mt-6 relative">
                          <Quote className="w-8 h-8 mb-3 opacity-10" style={{ color: NAVY }} />
                          <p className="text-slate-600 text-sm sm:text-base leading-[1.8] line-clamp-4">
                            {leader.bio}
                          </p>
                        </div>
                      )}

                      <div className="mt-8">
                        <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase group-hover:gap-3 transition-all duration-300" style={{ color: RED }}>
                          View Full Profile <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </Reveal>
            </div>
          </section>
        ))}

        {/* ── Thin transition ── */}
        <ThinSeparator />

        {/* ── EXECUTIVE BOARD ── */}
        {executive.length > 0 && (
          <section className="relative py-12 sm:py-16 overflow-hidden" data-testid="executive-section">
            <Img variant="event" src={MARBLE_TEX} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.03] mix-blend-overlay pointer-events-none" loading="lazy" />

            <div className="container-custom relative z-10">
              <SectionHeader category="executive" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 max-w-3xl mx-auto">
                {executive.map((leader, idx) => (
                  <Reveal key={leader.id} delay={idx * 0.12}>
                    <Link to={`/leadership/${leader.id}`} className="group block h-full" data-testid={`leader-${leader.id}`}>
                      <div className="relative h-full bg-white rounded-sm overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-1.5">
                        <div className="absolute top-0 left-0 right-0 h-[3px] z-10" style={{ background: `linear-gradient(to right, ${RED}, ${RED}60)` }} />

                        <div className="aspect-[4/5] sm:aspect-[3/4] relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY}, ${SLATE})` }}>
                          {getImgSrc(leader.image_url) ? (
                            <Img variant="leader" src={getImgSrc(leader.image_url)} alt={leader.name}
                              className="w-full h-full object-cover object-[center_15%] group-hover:scale-[1.04] transition-transform duration-700" />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <User className="w-20 h-20 text-white/15" />
                            </div>
                          )}
                          <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/50 to-transparent" />
                          <div className="absolute bottom-0 inset-x-0 p-5 sm:p-6">
                            <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1.5" style={{ color: `${GOLD}cc` }}>Executive Board</p>
                            <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight group-hover:text-white/90 transition-colors duration-300">
                              {leader.name}
                            </h3>
                          </div>
                        </div>

                        <div className="p-5 sm:p-6 flex items-center justify-between">
                          <p className="text-sm font-medium" style={{ color: RED }}>{leader.title}</p>
                          <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all duration-300" />
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Thin transition ── */}
        <ThinSeparator />

        {/* ── BOARD OF DIRECTORS ── */}
        {directors.length > 0 && (
          <section className="relative py-12 sm:py-16 overflow-hidden" data-testid="directors-section">
            <div className="absolute inset-0 opacity-[0.012]" style={{ backgroundImage: `radial-gradient(${NAVY} 0.5px, transparent 0.5px)`, backgroundSize: '24px 24px' }} />

            <div className="container-custom relative z-10">
              <SectionHeader category="director" />

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-5 max-w-5xl mx-auto">
                {directors.map((leader, idx) => (
                  <Reveal key={leader.id} delay={idx * 0.06}>
                    <Link to={`/leadership/${leader.id}`} className="group block h-full" data-testid={`leader-${leader.id}`}>
                      <div className="relative h-full bg-white rounded-sm overflow-hidden border border-slate-100 hover:border-transparent hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                        <div className="aspect-square sm:aspect-[4/5] relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY}, ${SLATE})` }}>
                          {getImgSrc(leader.image_url) ? (
                            <Img variant="leader" src={getImgSrc(leader.image_url)} alt={leader.name}
                              className="w-full h-full object-cover object-[center_15%] group-hover:scale-[1.05] transition-transform duration-600" />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <User className="w-12 h-12 text-white/15" />
                            </div>
                          )}
                          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/35 to-transparent" />
                        </div>
                        <div className="p-2.5 sm:p-4">
                          <h3 className="text-xs sm:text-[15px] font-bold leading-snug tracking-tight group-hover:text-[#CD1125] transition-colors duration-300" style={{ color: NAVY }}>
                            {leader.name}
                          </h3>
                          <p className="text-[10px] sm:text-xs text-slate-500 font-medium mt-0.5 sm:mt-1 leading-snug line-clamp-2">{leader.title}</p>
                          <div className="mt-2.5 h-[2px] w-0 group-hover:w-full transition-all duration-500" style={{ backgroundColor: `${RED}40` }} />
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Thin transition ── */}
        {committee.length > 0 && <ThinSeparator />}

        {/* ── COMMITTEE DIRECTORS ── */}
        {committee.length > 0 && (
          <section className="relative py-12 sm:py-16 overflow-hidden" data-testid="committee-section">
            <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: `repeating-linear-gradient(45deg, ${NAVY} 0px, ${NAVY} 1px, transparent 1px, transparent 50px)` }} />

            <div className="container-custom relative z-10">
              <SectionHeader category="committee" />

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5 max-w-4xl mx-auto">
                {committee.map((leader, idx) => (
                  <Reveal key={leader.id} delay={idx * 0.06}>
                    <Link to={`/leadership/${leader.id}`} className="group block h-full" data-testid={`leader-${leader.id}`}>
                      <div className="relative h-full bg-white rounded-sm overflow-hidden border border-slate-100 hover:border-transparent hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                        <div className="aspect-square sm:aspect-[4/5] relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY}, ${SLATE})` }}>
                          {getImgSrc(leader.image_url) ? (
                            <Img variant="leader" src={getImgSrc(leader.image_url)} alt={leader.name}
                              className="w-full h-full object-cover object-[center_15%] group-hover:scale-[1.05] transition-transform duration-600" />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <User className="w-12 h-12 text-white/15" />
                            </div>
                          )}
                          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/35 to-transparent" />
                        </div>
                        <div className="p-2.5 sm:p-4">
                          <h3 className="text-xs sm:text-[15px] font-bold leading-snug tracking-tight group-hover:text-[#CD1125] transition-colors duration-300" style={{ color: NAVY }}>
                            {leader.name}
                          </h3>
                          <p className="text-[10px] sm:text-xs text-slate-500 font-medium mt-0.5 sm:mt-1 leading-snug line-clamp-2">{leader.title}</p>
                          <div className="mt-2.5 h-[2px] w-0 group-hover:w-full transition-all duration-500" style={{ backgroundColor: `${RED}40` }} />
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>

      {/* ╔══════════════════════════════════════════════╗
          ║         BOTTOM CTA SECTION                   ║
          ╚══════════════════════════════════════════════╝ */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: NAVY_DEEP }} data-testid="leadership-cta">
        <div className="absolute inset-0">
          <Img variant="generic" src={HERO_TEXTURE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15 mix-blend-overlay" loading="lazy" />
          <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 50% at 50% 50%, ${NAVY}80, ${NAVY_DEEP}ff)` }} />
        </div>
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(to right, ${RED}00, ${GOLD}30, ${RED}00)` }} />

        <div className="container-custom relative z-10 text-center">
          <Reveal>
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="h-px w-8" style={{ backgroundColor: `${GOLD}40` }} />
              <span className="text-[10px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>Join Our Community</span>
              <div className="h-px w-8" style={{ backgroundColor: `${GOLD}40` }} />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-5">
              Ready to Make an Impact?
            </h2>
            <p className="text-white/40 text-sm sm:text-base max-w-lg mx-auto mb-10 leading-relaxed">
              Connect with our leadership team and become part of a premier network of Dominican business professionals in Florida.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Link to="/membership">
                <button className="w-full sm:w-auto px-8 py-3.5 text-white font-semibold rounded-sm transition-all duration-300 text-sm tracking-wide hover:brightness-110 hover:shadow-lg" style={{ backgroundColor: RED }} data-testid="leadership-cta-join">
                  Become a Member
                </button>
              </Link>
              <Link to="/contact">
                <button className="w-full sm:w-auto px-8 py-3.5 border border-white/20 text-white/70 hover:text-white hover:border-white/40 hover:bg-white/5 font-semibold rounded-sm transition-all duration-300 text-sm tracking-wide" data-testid="leadership-cta-contact">
                  Contact Us
                </button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default LeadershipPage;
